/**
 * V2 admin — Explorers tab (V1 admin-users.js parity).
 *
 * The full roster with search, sortable columns, column filters, role
 * changes, lock/unlock, email opt-in, tutorial-progress reset, Copy
 * Emails / Export CSV, preview (influencer) accounts with transfer, and
 * the CONFIRM DELETE purge. Email-bearing rows come from the admin-only
 * get_users_admin RPCs; destructive account work goes through the
 * purge-user PHP proxy (service key stays server-side).
 */
import * as gateway from '../../core/gateway.js';
import { escapeHtml, debounce } from '../../../assets/js/utils.js';
import { showToast } from '../../ui/toast.js';
import { confirmDialog } from './confirm.js';
import { resetProgress as resetOwnTutorial } from '../community/tutorial.js';
import { getApiBase } from '../../core/config.js';
import { SIGNUP_SOURCES, labelFor } from '../../core/signup-source.js';

const TUTORIAL_TOTAL = 12;
const API_BASE = getApiBase();

const dateStr = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { timeZone: 'America/Los_Angeles' }) : '—';

/** ">5", ">= 3", "=0", "!=2" — V1's numeric column filter. Exported for tests. */
export function parseNumericFilter(str) {
  const match = (str || '').trim().match(/^([><=!]{1,2})\s*(\d+)$/);
  if (!match) return null;
  return { op: match[1], val: parseInt(match[2], 10) };
}

export function matchesNumericFilter(value, filter) {
  switch (filter.op) {
    case '>': return value > filter.val;
    case '<': return value < filter.val;
    case '>=': return value >= filter.val;
    case '<=': return value <= filter.val;
    case '=': case '==': return value === filter.val;
    case '!=': return value !== filter.val;
    default: return true;
  }
}

function dateCutoff(preset) {
  const days = { '7d': 7, '30d': 30, '90d': 90, '1y': 365 }[preset];
  return days ? Date.now() - days * 86400000 : null;
}

/**
 * Apply search + column filters + sort to the roster (V1 applyUserFilter).
 * Exported for tests.
 */
export function filterUsers(users, { search = '', filters = {}, sortColumn = 'last_login', sortDirection = 'desc' } = {}) {
  let result = [...users];
  if (search) {
    const term = search.toLowerCase();
    result = result.filter((u) =>
      u.displayName.toLowerCase().includes(term) || (u.email || '').toLowerCase().includes(term));
  }
  if (filters.name) {
    const term = filters.name.toLowerCase();
    result = result.filter((u) => u.displayName.toLowerCase().includes(term));
  }
  if (filters.email) {
    const term = filters.email.toLowerCase();
    result = result.filter((u) => (u.email || '').toLowerCase().includes(term));
  }
  if (filters.role) result = result.filter((u) => (u.role || '') === filters.role);
  if (filters.yoinks) {
    const f = parseNumericFilter(filters.yoinks);
    if (f) result = result.filter((u) => matchesNumericFilter(u.yoinkCount, f));
  }
  if (filters.created_at) {
    const cutoff = dateCutoff(filters.created_at);
    if (cutoff) result = result.filter((u) => new Date(u.created_at).getTime() >= cutoff);
  }
  if (filters.last_login) {
    if (filters.last_login === 'never') {
      result = result.filter((u) => !u.last_login);
    } else {
      const cutoff = dateCutoff(filters.last_login);
      if (cutoff) result = result.filter((u) => u.last_login && new Date(u.last_login).getTime() >= cutoff);
    }
  }
  if (filters.wants_emails === 'yes') result = result.filter((u) => u.wants_emails !== false);
  else if (filters.wants_emails === 'no') result = result.filter((u) => u.wants_emails === false);
  if (filters.signup_source === 'none') result = result.filter((u) => !u.signup_source);
  else if (filters.signup_source) result = result.filter((u) => u.signup_source === filters.signup_source);
  if (filters.onboarding) {
    result = result.filter((u) => {
      const count = typeof u.onboarding_stage === 'number' ? u.onboarding_stage : 0;
      if (filters.onboarding === 'new') return count === 0;
      if (filters.onboarding === 'started') return count > 0 && count < TUTORIAL_TOTAL;
      if (filters.onboarding === 'complete') return count >= TUTORIAL_TOTAL;
      return true;
    });
  }

  const numeric = ['yoinks', 'onboarding', 'created_at', 'last_login'].includes(sortColumn);
  result.sort((a, b) => {
    const pick = (u) => {
      switch (sortColumn) {
        case 'name': return u.displayName.toLowerCase();
        case 'email': return (u.email || '').toLowerCase();
        case 'role': return (u.role || '').toLowerCase();
        case 'yoinks': return u.yoinkCount;
        case 'onboarding': return typeof u.onboarding_stage === 'number' ? u.onboarding_stage : 0;
        case 'last_login': return u.last_login ? new Date(u.last_login).getTime() : 0;
        case 'signup_source': return labelFor(u.signup_source).toLowerCase();
        default: return new Date(u.created_at).getTime();
      }
    };
    const aVal = pick(a);
    const bVal = pick(b);
    if (numeric) return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
  return result;
}

/** Emails for the Copy button: opted-in, real (non-preview) accounts only. */
export function copyableEmails(users) {
  return users
    .filter((u) => u.wants_emails !== false && u.role !== 'preview' && u.is_preview !== true)
    .map((u) => u.email)
    .filter((e) => e && e.trim());
}

export function usersCsv(users) {
  const esc = (val) => {
    if (!val) return '';
    const str = String(val);
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };
  const rows = users.map((u) => [
    esc(u.displayName), esc(u.email), esc(u.role), u.yoinkCount,
    `${typeof u.onboarding_stage === 'number' ? u.onboarding_stage : 0} / ${TUTORIAL_TOTAL}`,
    dateStr(u.created_at), u.last_login ? dateStr(u.last_login) : '',
    esc(labelFor(u.signup_source)), esc(u.signup_source_detail),
  ]);
  return ['Name,Email,Role,Yoinks,Tutorial,Joined,Last Login,Heard From,Heard From Detail', ...rows.map((r) => r.join(','))].join('\n');
}

/**
 * "How did you hear about us?" tally for the roster header — real
 * accounts only (a preview account is ours). Exported for tests.
 * @returns {{ total: number, answered: number, rows: {code, label, count}[] }}
 */
export function sourceTally(users) {
  const real = users.filter((u) => u.is_preview !== true && u.role !== 'preview');
  const counts = new Map();
  for (const u of real) {
    if (!u.signup_source) continue;
    counts.set(u.signup_source, (counts.get(u.signup_source) || 0) + 1);
  }
  const rows = [...counts.entries()]
    .map(([code, count]) => ({ code, label: labelFor(code), count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  return { total: real.length, answered: rows.reduce((n, r) => n + r.count, 0), rows };
}

async function authToken() {
  const session = await gateway.getSession();
  return session?.data?.session?.access_token || null;
}

async function purgeUser({ id, email }) {
  const token = await authToken();
  if (!token) throw new Error('Not authenticated');
  const body = email ? { email, dry_run: false } : { user_id: id, dry_run: false };
  const response = await fetch(`${API_BASE}/purge-user.php`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const result = await response.json();
  if (!response.ok || !result.success) throw new Error(result.error || 'Failed to purge user');
  return result;
}

export function mount(el, ctx) {
  const { store, router } = ctx;
  let destroyed = false;
  let all = [];
  let loaded = false;
  let search = '';
  let filters = {};
  let showFilters = false;
  let sortColumn = 'last_login';
  let sortDirection = 'desc';
  let showPreviewForm = false;
  let transferFrom = null; // { id, name } preview account being transferred

  function shown() {
    return filterUsers(all, { search, filters, sortColumn, sortDirection });
  }

  async function load() {
    const combined = await gateway.rpc('get_users_admin_with_counts');
    if (!combined.error && combined.data) {
      all = combined.data.map((u) => ({
        ...u,
        yoinkCount: parseInt(u.yoink_count) || 0,
        displayName: u.full_name || u.username || '(No name)',
      }));
    } else {
      const rpcResult = await gateway.rpc('get_users_admin');
      if (rpcResult.error) throw rpcResult.error;
      const counts = {};
      const countsResult = await gateway.rpc('get_user_yoink_counts');
      (countsResult.data || []).forEach((row) => {
        if (row.user_id) counts[row.user_id] = parseInt(row.yoink_count) || 0;
      });
      all = (rpcResult.data || []).map((u) => ({
        ...u,
        yoinkCount: counts[u.id] || 0,
        displayName: u.full_name || u.username || '(No name)',
      }));
    }
    loaded = true;
  }

  const arrow = (col) => (sortColumn === col ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : '');
  const activeFilters = () => Object.values(filters).filter((v) => v).length;

  function filterRowHtml() {
    const sel = (key, options) => `
      <select class="admin-filter" data-ufilter="${key}" aria-label="Filter by ${key.replace('_', ' ')}">
        <option value="">All</option>
        ${options.map(([v, label]) => `<option value="${v}" ${filters[key] === v ? 'selected' : ''}>${label}</option>`).join('')}
      </select>`;
    const dateOptions = [['7d', 'Last 7d'], ['30d', 'Last 30d'], ['90d', 'Last 90d'], ['1y', 'Last year']];
    return `
      <tr class="admin-filter-row">
        <th><input type="text" class="admin-filter" data-ufilter="name" aria-label="Filter by name" placeholder="contains…" value="${escapeHtml(filters.name || '')}"></th>
        <th><input type="text" class="admin-filter" data-ufilter="email" aria-label="Filter by email" placeholder="contains…" value="${escapeHtml(filters.email || '')}"></th>
        <th>${sel('role', [['admin', 'admin'], ['enduser', 'enduser'], ['preview', 'preview']])}</th>
        <th><input type="text" class="admin-filter admin-filter-narrow" data-ufilter="yoinks" aria-label="Filter by yoink count" placeholder="&gt;0, =5…" value="${escapeHtml(filters.yoinks || '')}"></th>
        <th>${sel('onboarding', [['new', 'New (0)'], ['started', 'Started'], ['complete', 'Complete']])}</th>
        <th>${sel('created_at', dateOptions)}</th>
        <th>${sel('last_login', [...dateOptions, ['never', 'Never']])}</th>
        <th>${sel('signup_source', [...SIGNUP_SOURCES.map((s) => [s.code, s.label]), ['none', 'Not answered']])}</th>
        <th>${sel('wants_emails', [['yes', 'Opted in'], ['no', 'Opted out']])}</th>
        <th>${activeFilters() ? '<button type="button" class="admin-btn admin-btn-xs" data-clear-ufilters>Clear</button>' : ''}</th>
      </tr>`;
  }

  function rowHtml(u) {
    const isSelf = u.id === store.get().user?.id;
    const isPreview = u.is_preview === true || u.role === 'preview';
    const stage = typeof u.onboarding_stage === 'number' ? u.onboarding_stage : 0;
    // Always a button, even at 0 (V1 admin-users.js:32) — a self-reset at
    // 0 still matters: it clears THIS browser's localStorage copy, which
    // the server-side reset can't reach
    const tutorialPill = `<button type="button" class="admin-pill admin-pill-btn ${stage >= TUTORIAL_TOTAL ? 'admin-pill-done' : ''}"
          data-reset-tutorial="${u.id}" data-user-name="${escapeHtml(u.displayName)}"
          title="Tap to reset tutorial progress">${stage >= TUTORIAL_TOTAL ? 'Complete' : `${stage} / ${TUTORIAL_TOTAL}`}</button>`;
    return `
      <tr class="${u.is_locked ? 'admin-row-locked' : ''} ${isSelf ? 'admin-row-self' : ''}">
        <td>
          <button type="button" class="admin-link" data-open-user="${escapeHtml(u.username || '')}">${escapeHtml(u.displayName)}</button>
          ${isPreview ? '<span class="admin-badge">PREVIEW</span>' : ''}
        </td>
        <td class="admin-cell-dim">${isPreview ? '<em>placeholder</em>' : escapeHtml(u.email || '—')}</td>
        <td>
          <select class="admin-filter" data-role-user="${u.id}" ${isSelf ? 'disabled' : ''}>
            ${['admin', 'enduser', 'preview'].map((r) => `<option value="${r}" ${u.role === r ? 'selected' : ''}>${r}</option>`).join('')}
          </select>
        </td>
        <td class="admin-num">${u.yoinkCount}</td>
        <td>${tutorialPill}</td>
        <td class="admin-cell-dim">${dateStr(u.created_at)}</td>
        <td class="admin-cell-dim">${dateStr(u.last_login)}</td>
        <td class="admin-cell-dim admin-cell-source">${u.signup_source ? escapeHtml(labelFor(u.signup_source)) : '—'}${u.signup_source_detail ? `<small>${escapeHtml(u.signup_source_detail)}</small>` : ''}</td>
        <td class="admin-cell-center">
          <input type="checkbox" data-wants-emails="${u.id}" ${u.wants_emails !== false ? 'checked' : ''}
            title="${u.wants_emails !== false ? 'Opted in to emails' : 'Opted out of emails'}">
        </td>
        <td>
          <div class="admin-actions">
            ${isPreview ? `
              <button type="button" class="admin-btn admin-btn-xs" data-log-as="${u.id}"
                title="Log a yoink for this preview account">+ Yoink</button>
              <button type="button" class="admin-btn admin-btn-xs" data-transfer-user="${u.id}"
                data-user-name="${escapeHtml(u.displayName)}">Transfer</button>` : ''}
            <button type="button" class="admin-btn admin-btn-xs" data-lock-user="${u.id}" ${isSelf ? 'disabled' : ''}
              title="${u.is_locked ? 'Unlock' : 'Lock'}">${u.is_locked ? '🔒' : '🔓'}</button>
            <button type="button" class="admin-btn admin-btn-xs admin-btn-danger" data-delete-user="${u.id}"
              data-user-name="${escapeHtml(u.displayName)}" data-user-email="${escapeHtml(u.email || '')}"
              ${isSelf ? 'disabled' : ''}>Delete</button>
          </div>
        </td>
      </tr>`;
  }

  function transferListHtml() {
    const term = (el.querySelector('[data-transfer-search]')?.value || '').toLowerCase();
    const real = all.filter((u) => u.is_preview !== true && u.role !== 'preview')
      .filter((u) => !term ||
        u.displayName.toLowerCase().includes(term) ||
        (u.email || '').toLowerCase().includes(term) ||
        (u.username || '').toLowerCase().includes(term));
    if (!real.length) return '<p class="admin-muted">No explorers match.</p>';
    return real.map((u) => `
      <button type="button" class="admin-transfer-row" data-transfer-to="${u.id}">
        <span>${escapeHtml(u.displayName)}<small>${escapeHtml(u.email || '')}</small></span>
        <span class="admin-cell-dim">${u.yoinkCount} yoinks</span>
      </button>`).join('');
  }

  function render() {
    if (!loaded) {
      el.innerHTML = '<div class="v2-loading">Opening the roster…</div>';
      return;
    }
    const list = shown();
    const previewCount = all.filter((u) => u.is_preview === true || u.role === 'preview').length;
    const tally = sourceTally(all);
    const tallyHtml = tally.rows.length
      ? `<p class="admin-source-tally" title="Self-reported at signup or on the first sign-in">
          <span>Heard from · ${tally.answered} of ${tally.total} answered:</span>
          ${tally.rows.map((r) => `<span><strong>${r.count}</strong> ${escapeHtml(r.label)}</span>`).join('')}
        </p>`
      : '';
    el.innerHTML = `
      <div class="admin-card admin-card-span">
        <div class="admin-toolbar">
          <input type="search" class="admin-search" data-user-search aria-label="Search explorers" placeholder="Search explorers…"
            value="${escapeHtml(search)}" autocomplete="off">
          <span class="admin-muted">${list.length} of ${all.length}${previewCount ? ` (${previewCount} preview)` : ''}${list.length < all.length ? ` · ${all.length - list.length} hidden by search/filters` : ''}</span>
          <span class="admin-toolbar-spacer"></span>
          <button type="button" class="admin-btn" data-new-preview>+ Preview account</button>
          <button type="button" class="admin-btn ${activeFilters() ? 'admin-btn-active' : ''}" data-toggle-ufilters>
            Filter${activeFilters() ? ` (${activeFilters()})` : ''}</button>
          <button type="button" class="admin-btn" data-copy-emails>Copy emails</button>
          <button type="button" class="admin-btn" data-export-csv>Export CSV</button>
        </div>
        ${tallyHtml}
        ${showPreviewForm ? `
        <form class="admin-preview-form" data-preview-form>
          <span class="admin-muted">preview-</span>
          <input type="text" data-preview-handle aria-label="Influencer handle" placeholder="handle (e.g. beerhounddrinks)" maxlength="30" autocomplete="off">
          <button type="submit" class="admin-btn">Create</button>
          <button type="button" class="admin-btn" data-preview-cancel>Cancel</button>
          <p class="admin-muted admin-form-hint">Type the influencer handle — the "preview-" prefix is added
            automatically. Transfer everything to their real account later.</p>
        </form>` : ''}
        <div class="admin-table-wrap">
          <table class="admin-table">
            <thead>
              <tr>
                ${[['name', 'Name'], ['email', 'Email'], ['role', 'Role'], ['yoinks', 'Yoinks'],
                  ['onboarding', 'Tutorial'], ['created_at', 'Joined'], ['last_login', 'Last login'],
                  ['signup_source', 'Heard from']]
                  .map(([col, label]) => `<th class="admin-sortable" data-sort-users="${col}">${label}${arrow(col)}</th>`).join('')}
                <th title="Include in Copy Emails">Emails</th>
                <th>Actions</th>
              </tr>
              ${showFilters ? filterRowHtml() : ''}
            </thead>
            <tbody>
              ${list.length ? list.map(rowHtml).join('') : '<tr><td colspan="10" class="admin-empty-cell">No explorers found</td></tr>'}
            </tbody>
          </table>
        </div>
      </div>
      ${transferFrom ? `
      <dialog class="v2-auth-dialog admin-transfer-dialog" data-transfer-dialog>
        <h2 class="v2-dialog-title">Transfer preview account</h2>
        <p class="admin-muted">Everything from <strong>${escapeHtml(transferFrom.name)}</strong> moves to the
          explorer you pick; the preview account is then deleted.</p>
        <input type="search" data-transfer-search aria-label="Search explorers to transfer to" placeholder="Search explorers…" autocomplete="off">
        <div class="admin-transfer-list" data-transfer-list>${transferListHtml()}</div>
        <div class="admin-confirm-actions">
          <button type="button" class="admin-btn" data-transfer-cancel>Cancel</button>
        </div>
      </dialog>` : ''}`;

    const dialog = el.querySelector('[data-transfer-dialog]');
    if (dialog && typeof dialog.showModal === 'function') dialog.showModal();

    // Debounced text inputs re-render, so focus + caret must survive (V1)
    const wireDebounced = (selector, apply) => {
      el.querySelectorAll(selector).forEach((input) => {
        input.addEventListener('input', debounce((e) => {
          const caret = e.target.selectionStart;
          const key = e.target.dataset.ufilter;
          apply(e.target.value, key);
          render();
          const again = key
            ? el.querySelector(`${selector}[data-ufilter="${key}"]`)
            : el.querySelector(selector);
          if (again) { again.focus(); again.setSelectionRange(caret, caret); }
        }, 300));
      });
    };
    wireDebounced('[data-user-search]', (value) => { search = value; });
    wireDebounced('input.admin-filter[data-ufilter]', (value, key) => { filters[key] = value; });
    el.querySelector('[data-transfer-search]')?.addEventListener('input', debounce(() => {
      const listEl = el.querySelector('[data-transfer-list]');
      if (listEl) listEl.innerHTML = transferListHtml();
    }, 200));
  }

  async function changeRole(userId, newRole, selectEl) {
    const isPreviewRole = newRole === 'preview';
    const body = { role: newRole, is_preview: isPreviewRole };
    if (isPreviewRole) body.wants_emails = false;
    const { error } = await gateway.rawUpdate(gateway.TABLES.PROFILES, {
      filters: { id: `eq.${userId}` }, body,
    });
    if (destroyed) return;
    if (error) {
      showToast('Couldn’t update the role.', { type: 'info' });
      const u = all.find((x) => x.id === userId);
      if (u && selectEl) selectEl.value = u.role;
      return;
    }
    const u = all.find((x) => x.id === userId);
    if (u) {
      u.role = newRole;
      u.is_preview = isPreviewRole;
      if (isPreviewRole) u.wants_emails = false;
    }
    showToast('Role updated.', { type: 'success' });
    render();
  }

  async function toggleLock(userId) {
    const u = all.find((x) => x.id === userId);
    if (!u) return;
    const newLocked = !u.is_locked;
    if (newLocked && !(await confirmDialog({
      title: `Lock ${u.displayName}’s account?`,
      message: 'They will not be able to sign in until unlocked.',
      confirmLabel: 'Lock account',
    }))) return;
    const { error } = await gateway.rawUpdate(gateway.TABLES.PROFILES, {
      filters: { id: `eq.${userId}` }, body: { is_locked: newLocked },
    });
    if (destroyed) return;
    if (error) return showToast('Couldn’t update the account.', { type: 'info' });
    u.is_locked = newLocked;
    showToast(`Account ${newLocked ? 'locked' : 'unlocked'}.`, { type: 'success' });
    render();
  }

  async function resetTutorial(userId, name) {
    if (!(await confirmDialog({
      title: `Reset tutorial for ${name}?`,
      message: 'Their progress goes back to 0 and the Getting Started checklist reappears on their next visit.',
      confirmLabel: 'Reset progress',
    }))) return;
    const { error } = await gateway.rpc('reset_user_onboarding', { p_user_id: userId });
    if (destroyed) return;
    if (error) return showToast('Couldn’t reset tutorial progress.', { type: 'info' });
    const u = all.find((x) => x.id === userId);
    if (u) u.onboarding_stage = 0;
    if (store.get().user?.id === userId) {
      // Clear the in-memory profile copy too — main.js re-merges
      // profile.tutorial_progress into localStorage on every store
      // change, which would silently undo the reset
      const profile = store.get().profile;
      if (profile?.tutorial_progress) store.set({ profile: { ...profile, tutorial_progress: {} } });
      resetOwnTutorial();
    }
    showToast(`Tutorial progress reset for ${name}.`, { type: 'success' });
    render();
  }

  async function toggleWantsEmails(userId, wants, checkbox) {
    const { error } = await gateway.rawUpdate(gateway.TABLES.PROFILES, {
      filters: { id: `eq.${userId}` }, body: { wants_emails: wants },
    });
    if (destroyed) return;
    if (error) {
      checkbox.checked = !wants;
      return showToast('Couldn’t update the email preference.', { type: 'info' });
    }
    const u = all.find((x) => x.id === userId);
    if (u) u.wants_emails = wants;
    checkbox.title = wants ? 'Opted in to emails' : 'Opted out of emails';
    showToast(`${u?.displayName || 'Explorer'} opted ${wants ? 'in to' : 'out of'} emails.`, { type: 'success' });
  }

  function copyEmails() {
    // Always the whole roster, not the filtered view — this button feeds
    // mailings to every opted-in member regardless of what's on screen
    const emails = copyableEmails(all);
    if (!emails.length) return showToast('No opted-in emails.', { type: 'info' });
    navigator.clipboard.writeText(emails.join(', ')).then(
      () => showToast(`Copied all ${emails.length} opted-in emails (preview + opted-out excluded).`, { type: 'success' }),
      () => showToast('Couldn’t reach the clipboard.', { type: 'info' }),
    );
  }

  function exportCsv() {
    const list = shown();
    if (!list.length) return showToast('Nothing to export.', { type: 'info' });
    const blob = new Blob([usersCsv(list)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `yoink-users-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    showToast(`Exported ${list.length} explorers.`, { type: 'success' });
  }

  async function createPreview(handleRaw) {
    const handle = (handleRaw || '').trim().replace(/^preview[-_]?/i, '');
    if (!handle) return showToast('Type the influencer handle first.', { type: 'info' });
    if (!/^[a-zA-Z0-9_-]{2,30}$/.test(handle)) {
      return showToast('Handles are 2–30 letters, numbers, underscores, or hyphens.', { type: 'info' });
    }
    const username = `preview-${handle.toLowerCase()}`;
    if (all.some((u) => (u.username || '').toLowerCase() === username)) {
      return showToast(`"${username}" already exists.`, { type: 'info' });
    }
    try {
      const token = await authToken();
      if (!token) throw new Error('Not authenticated');
      const response = await fetch(`${API_BASE}/create-preview-user.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ username }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.error || 'Create failed');
      if (destroyed) return;
      showToast(`Preview account "${username}" created.`, { type: 'success' });
      showPreviewForm = false;
      await load();
      if (!destroyed) render();
    } catch (err) {
      if (!destroyed) showToast(err.message || 'Couldn’t create the preview account.', { type: 'info' });
    }
  }

  async function transferTo(realUserId) {
    const real = all.find((u) => u.id === realUserId);
    const from = transferFrom;
    if (!real || !from) return;
    if (!(await confirmDialog({
      title: `Transfer "${from.name}" → "${real.displayName}"?`,
      message: 'All yoinks move over, profile details copy where the real account is blank, and the preview account is deleted. This can’t be undone.',
      confirmLabel: 'Transfer everything',
    }))) return;
    try {
      const { data, error } = await gateway.rpc('transfer_preview_account', {
        p_preview_user_id: from.id,
        p_real_user_id: realUserId,
      });
      if (error) throw error;
      try {
        const preview = all.find((u) => u.id === from.id);
        await purgeUser({ id: from.id, email: preview?.email });
        showToast(`Transferred ${data?.yoinks_moved || 0} yoinks to ${real.displayName}.`, { type: 'success' });
      } catch {
        showToast(`Content transferred, but "${from.name}" needs deleting by hand.`, { type: 'info' });
      }
      if (destroyed) return;
      transferFrom = null;
      await load();
      if (!destroyed) render();
    } catch (err) {
      if (!destroyed) showToast(err.message || 'Transfer failed.', { type: 'info' });
    }
  }

  async function deleteUser(userId, name, email) {
    if (!(await confirmDialog({
      title: `Permanently delete ${name}?`,
      message: 'Their sign-in, profile, and every yoink and photo go with them. This cannot be undone.',
      confirmLabel: 'Delete forever',
      requirePhrase: true,
    }))) return;
    try {
      await purgeUser({ id: userId, email });
      if (destroyed) return;
      showToast(`"${name}" permanently deleted.`, { type: 'success' });
      await load();
      if (!destroyed) render();
    } catch (err) {
      if (!destroyed) showToast(err.message || 'Delete failed.', { type: 'info' });
    }
  }

  function onClick(event) {
    const t = event.target;
    const sortTh = t.closest('[data-sort-users]');
    if (sortTh) {
      const col = sortTh.dataset.sortUsers;
      if (sortColumn === col) sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      else { sortColumn = col; sortDirection = col === 'created_at' || col === 'last_login' ? 'desc' : 'asc'; }
      return render();
    }
    if (t.closest('[data-toggle-ufilters]')) { showFilters = !showFilters; return render(); }
    if (t.closest('[data-clear-ufilters]')) { filters = {}; return render(); }
    if (t.closest('[data-copy-emails]')) return copyEmails();
    if (t.closest('[data-export-csv]')) return exportCsv();
    if (t.closest('[data-new-preview]')) {
      showPreviewForm = !showPreviewForm;
      render();
      if (showPreviewForm) setTimeout(() => el.querySelector('[data-preview-handle]')?.focus(), 50);
      return;
    }
    if (t.closest('[data-preview-cancel]')) { showPreviewForm = false; return render(); }
    const openUser = t.closest('[data-open-user]');
    if (openUser?.dataset.openUser) return router.navigate('user', { username: openUser.dataset.openUser });
    const lockBtn = t.closest('[data-lock-user]');
    if (lockBtn) return toggleLock(lockBtn.dataset.lockUser);
    const resetBtn = t.closest('[data-reset-tutorial]');
    if (resetBtn) return resetTutorial(resetBtn.dataset.resetTutorial, resetBtn.dataset.userName);
    const logAs = t.closest('[data-log-as]');
    if (logAs) {
      // The log page reads ?as= to file the yoink under the preview
      // account — the param must be on the URL before the view mounts
      const url = `${router.generate('log')}?as=${encodeURIComponent(logAs.dataset.logAs)}`;
      window.history.pushState({ name: 'log', params: {} }, '', url);
      window.dispatchEvent(new PopStateEvent('popstate'));
      return;
    }
    const transferBtn = t.closest('[data-transfer-user]');
    if (transferBtn) {
      transferFrom = { id: transferBtn.dataset.transferUser, name: transferBtn.dataset.userName };
      render();
      setTimeout(() => el.querySelector('[data-transfer-search]')?.focus(), 50);
      return;
    }
    if (t.closest('[data-transfer-cancel]')) {
      el.querySelector('[data-transfer-dialog]')?.close?.();
      transferFrom = null;
      return render();
    }
    const transferToBtn = t.closest('[data-transfer-to]');
    if (transferToBtn) return transferTo(transferToBtn.dataset.transferTo);
    const deleteBtn = t.closest('[data-delete-user]');
    if (deleteBtn) {
      return deleteUser(deleteBtn.dataset.deleteUser, deleteBtn.dataset.userName, deleteBtn.dataset.userEmail);
    }
  }

  function onChange(event) {
    const roleSel = event.target.closest('[data-role-user]');
    if (roleSel) return changeRole(roleSel.dataset.roleUser, roleSel.value, roleSel);
    const wantsBox = event.target.closest('[data-wants-emails]');
    if (wantsBox) return toggleWantsEmails(wantsBox.dataset.wantsEmails, wantsBox.checked, wantsBox);
    const filterSel = event.target.closest('select.admin-filter[data-ufilter]');
    if (filterSel) {
      filters[filterSel.dataset.ufilter] = filterSel.value;
      return render();
    }
  }

  function onSubmit(event) {
    if (event.target.closest('[data-preview-form]')) {
      event.preventDefault();
      createPreview(el.querySelector('[data-preview-handle]')?.value);
    }
  }

  render();
  load().then(() => { if (!destroyed) render(); })
    .catch(() => { if (!destroyed) el.innerHTML = '<div class="v2-error">Couldn’t load the roster.</div>'; });

  el.addEventListener('click', onClick);
  el.addEventListener('change', onChange);
  el.addEventListener('submit', onSubmit);

  return () => {
    destroyed = true;
    el.removeEventListener('click', onClick);
    el.removeEventListener('change', onChange);
    el.removeEventListener('submit', onSubmit);
  };
}
